import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cookie, Share2, MapPin, Lock, ClipboardCheck, MessageCircle, Mail, Settings, Share } from 'lucide-react';
import { ContactFormModal } from './ContactFormModal';
import { ShareInfoModal } from './ShareInfoModal';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { AdPreferencesModal } from './AdPreferencesModal';
import { CookiesModal } from './CookiesModal';
import { DoNotSellModal } from './DoNotSellModal';
import { StaticOneTrustIcon } from './StaticOneTrustIcon';
import { WorkInProgress } from './WorkInProgress';

export function PrivacyCenter() {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);
  const [isAdPreferencesModalOpen, setIsAdPreferencesModalOpen] = React.useState(false);
  const [isCookiesModalOpen, setIsCookiesModalOpen] = React.useState(false);
  const [isDoNotSellModalOpen, setIsDoNotSellModalOpen] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        <WorkInProgress className="mb-8" />

        <div className="text-center mb-12">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Learn more about how we handle consumer data.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            For more information on the collection and use of your information, please access our{' '}
            <Link to="/privacy" className="text-[#5e9bb8] hover:text-[#499da8] dark:text-[#5e9bb8] dark:hover:text-[#499da8] transition-colors duration-200">
              Online Privacy & Security Policy
            </Link>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => navigate('/cookies')}
          >
            <Cookie className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Cookies</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => setIsDoNotSellModalOpen(true)}
          >
            <Share2 className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Do Not Sell My Data</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer relative"
            onClick={() => navigate('/data-collection')}
          >
            <MapPin className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Data Collection Management</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
            <div className="absolute top-4 right-4">
              <StaticOneTrustIcon size="lg" />
            </div>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer relative"
            onClick={() => navigate('/privacy-rights')}
          >
            <Lock className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Privacy Rights</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
            <div className="absolute top-4 right-4">
              <StaticOneTrustIcon size="lg" />
            </div>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => setIsPolicyModalOpen(true)}
          >
            <ClipboardCheck className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Privacy Policy</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer relative"
            onClick={() => navigate('/communication-preferences')}
          >
            <MessageCircle className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Communication Preferences</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
            <div className="absolute top-4 right-4">
              <StaticOneTrustIcon size="lg" />
            </div>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => setIsContactModalOpen(true)}
          >
            <Mail className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Contact Us</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => setIsShareModalOpen(true)}
          >
            <Share className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">How We Share Information</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>

          <div 
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 ease-in-out flex flex-col items-center justify-center w-64 h-64 cursor-pointer"
            onClick={() => setIsAdPreferencesModalOpen(true)}
          >
            <Settings className="w-16 h-16 text-[#5e9bb8] mb-4" />
            <h3 className="text-gray-800 dark:text-white font-medium text-center mb-2">Ad Preferences</h3>
            <span className="text-[#5e9bb8] text-sm hover:underline">Learn More</span>
          </div>
        </div>
      </div>

      <ContactFormModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />

      <ShareInfoModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      <PrivacyPolicyModal
        isOpen={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
      />

      <AdPreferencesModal
        isOpen={isAdPreferencesModalOpen}
        onClose={() => setIsAdPreferencesModalOpen(false)}
      />

      <CookiesModal
        isOpen={isCookiesModalOpen}
        onClose={() => setIsCookiesModalOpen(false)}
      />

      <DoNotSellModal
        isOpen={isDoNotSellModalOpen}
        onClose={() => setIsDoNotSellModalOpen(false)}
      />
    </div>
  );
}